/**
 * Skills Repository API service.
 *
 * The Skills Repository used to be hardcoded in src/api/skillsRepository.js.
 * After Phase 8 of the IEP migration, the data lives in clinical.skill_domain
 * and clinical.skill, exposed via /api/v1/skills/*. Components should call
 * these functions instead of importing the JS arrays directly.
 */

import { get, post, put, del, upload, download } from './client';

/**
 * List all skill domains.
 * @returns {Promise<Array<{id, code, name, description, display_order}>>}
 */
export const listSkillDomains = () => get('/skills/domains');

/**
 * List skills, optionally filtered by domain code/id or a free-text query.
 * Server caps the response at 1000 rows (currently fits the whole catalogue).
 * @param {Object} [params]
 * @param {string} [params.domain_code] e.g. "MAND" (without the DOMAIN- prefix)
 * @param {string} [params.domain_id]   UUID — alternative to domain_code
 * @param {string} [params.q]           Free-text search on name or code
 * @returns {Promise<Array<{id, code, name, description, domain_id, level, labels}>>}
 */
export const listSkills = (params = {}) => get('/skills', params);

/**
 * Get the assembled Domain → Sub-Domain → Skill / Target hierarchy tree.
 * Each node carries skills[], targets[], sub_domains[] and roll-up counts.
 * @param {Object} [params]
 * @param {string} [params.domain_code] Scope to one top-level domain (lazy load)
 * @returns {Promise<Array<Object>>}
 */
export const getSkillHierarchy = (params = {}) => get('/skills/hierarchy', params);

/**
 * List institutional Target Library entries (clinical.skill_target_bank).
 * @param {Object} [params]
 * @param {string} [params.domain_id] Filter by domain / sub-domain id
 * @param {string} [params.skill_id]  Filter by associated skill id
 * @returns {Promise<Array<{id, domain_id, skill_id, name, materials, display_order}>>}
 */
export const listSkillTargets = (params = {}) => get('/skills/targets', params);

// --- Repository mutations (admin / Clinical Head) ---------------------------

/** Version history for a repository goal (newest first). */
export const listSkillVersions = (skillId) => get(`/skills/${skillId}/versions`);

/** Add a skill/goal to the repository (creates v1). */
export const createSkill = (data) => post('/skills', data);

/** Edit a goal — writes a new version and repoints current_version_id. */
export const editSkill = (skillId, data) => put(`/skills/${skillId}`, data);

/** Soft-delete (retire) a goal. */
export const deleteSkill = (skillId) => del(`/skills/${skillId}`);

/** Restore a retired goal. */
export const restoreSkill = (skillId) => post(`/skills/${skillId}/restore`);

/** Add a top-level skill domain. */
export const createDomain = (data) => post('/skills/domains', data);

/** Add a sub-domain (child of a domain). */
export const createSubdomain = (data) => post('/skills/subdomains', data);

/** Rename a sub-domain. The code is never changed - see the endpoint's note. */
export const editSubdomain = (subId, data) => put(`/skills/subdomains/${subId}`, data);

/** Retire a sub-domain. Refused by the API while it still holds skills or targets. */
export const deleteSubdomain = (subId) => del(`/skills/subdomains/${subId}`);

/** Add a Target Library entry. */
export const createSkillTarget = (data) => post('/skills/targets', data);

/** Delete (retire) a Target Library entry. */
export const deleteSkillTarget = (targetId) => del(`/skills/targets/${targetId}`);

// --- Approval workflow (admin / Clinical Head) ------------------------------

/** Approve a pending skill change. */
export const approveSkill = (skillId, note) => post(`/skills/${skillId}/approve`, note ? { note } : {});
/** Reject a pending skill change. */
export const rejectSkill = (skillId, note) => post(`/skills/${skillId}/reject`, note ? { note } : {});
/** Approve a pending target change. */
export const approveTarget = (targetId, note) => post(`/skills/targets/${targetId}/approve`, note ? { note } : {});
/** Reject a pending target change. */
export const rejectTarget = (targetId, note) => post(`/skills/targets/${targetId}/reject`, note ? { note } : {});
/** Restore a retired/rejected target. */
export const restoreTarget = (targetId) => post(`/skills/targets/${targetId}/restore`);
/** Approve a pending sub-domain change. */
export const approveSubdomain = (subId, note) => post(`/skills/subdomains/${subId}/approve`, note ? { note } : {});
/** Reject a pending sub-domain change. */
export const rejectSubdomain = (subId, note) => post(`/skills/subdomains/${subId}/reject`, note ? { note } : {});
/** Restore a retired/rejected sub-domain. */
export const restoreSubdomain = (subId) => post(`/skills/subdomains/${subId}/restore`);
/** List all items waiting for approval (admin queue). */
export const listPendingChanges = () => get('/skills/pending');

/** Download the Excel import template (returns a Blob). kind = 'skills' | 'targets'. */
export const downloadImportTemplate = (kind) => download('/skills/import/template', { kind });

/**
 * Bulk-import an .xlsx file. kind = 'skills' | 'targets'.
 * dryRun=true previews the outcome without saving anything; false applies it.
 * Returns the summary.
 */
export const importRepository = (kind, file, dryRun = false, onProgress) => {
  const fd = new FormData();
  fd.append('kind', kind);
  fd.append('file', file);
  fd.append('dry_run', dryRun ? 'true' : 'false');
  return upload('/skills/import', fd, onProgress);
};

export default {
  listSkillDomains,
  listSkills,
  getSkillHierarchy,
  listSkillTargets,
  listSkillVersions,
  createSkill,
  editSkill,
  deleteSkill,
  restoreSkill,
  createSubdomain,
  editSubdomain,
  deleteSubdomain,
  createSkillTarget,
  deleteSkillTarget,
  approveSkill,
  rejectSkill,
  approveTarget,
  rejectTarget,
  restoreTarget,
  approveSubdomain,
  rejectSubdomain,
  restoreSubdomain,
  listPendingChanges,
  downloadImportTemplate,
  importRepository
};
